<template>
  <form @submit.prevent="handleContinue" action="#" class="marketplace-checkout--payment-method">
    <SloLoader v-if="!showPaymentForm" :loading="paymentDetailsLoading" size="xl">
      <div class="marketplace-checkout--table">
        <div class="marketplace-checkout--table-header flex items-center justify-between flex-wrap gap-sm">
          <h2 class="text-xl fw-strong">{{ 'Payment Method' | t }}</h2>
          <button
            v-if="authenticated && !showPaymentForm"
            @click="showPaymentForm = true"
            type="button"
            class="flex items-center justify-start gap-xs p-0 bg-transparent c-blue-400 hover:c-blue-700"
          >
            <SloIcon name="Plus" />
            {{ 'Add New Payment Method' | t }}
          </button>
        </div>
        <div class="marketplace-checkout--table-body padding-full">
          <ul v-if="paymentDetails.length" class="marketplace-checkout--saved-payment-methods my-xs">
            <li
              v-for="(paymentDetail, index) in paymentDetails"
              :key="`payment-detail-${index}`"
              :class="{
                'marketplace-checkout--saved-payment-method': true,
                'checked': isPaymentMethodChecked(paymentDetail)
              }"
            >
              <input
                type="radio"
                :id="`payment-detail-${index}`"
                :value="paymentDetail.id"
                v-model="paymentDetailId"
                :checked="isPaymentMethodChecked(paymentDetail)"
                :disabled="isCardExpired(paymentDetail)"
              />
              <SloIcon
                :name="paymentDetail?.cardType.name | getCardTypeIconName"
                size="lg"
                class="card-icon"
              />
              <label :for="`payment-detail-${index}`">
                <span class="saved-payment-method--title">
                  <span class="text-capitalize">{{ paymentDetail?.cardType.code }}</span>
                  {{ paymentDetail?.cardNumber | getCardNumberLastFourDigits }}
                  {{ paymentDetail?.defaultPayment ? ' (default)' : '' }}
                </span>
                <span :class="{'card-is-expired c-red-400': isCardExpired(paymentDetail)}">
                  {{ 'Expires' | t }}: {{ paymentDetail?.expiryMonth }}/{{ paymentDetail?.expiryYear }}
                  <span v-if="isCardExpired(paymentDetail)">{{ '(expired)' | t }}</span>
                </span>
                <span>{{ paymentDetail?.billingAddress | formatSingleLineAddress }}</span>
              </label>
              <SloIcon name="CircleCheckSolid" />
            </li>
          </ul>
        </div>
      </div>
    </SloLoader>
    <SloLoader v-if="showPaymentForm" :loading="paymetricAccessInfoLoading" size="xl">
      <div class="marketplace-checkout--table">
        <header class="marketplace-checkout--table-header">
          <h2 id="new-payment-details" class="text-xl fw-strong">{{ 'New payment details' | t }}</h2>
        </header>
        <div class="marketplace-checkout--table-body padding-full">
          <div class="paymetric-container">
            <iframe
              :src="paymetricAccessInfo.paymetricsIframeAccessTokenUrl"
              id="paymetric-iframe"
              name="paymetric-iframe"
              width="100%"
              height="100%"
              frameborder="0"
            ></iframe>
          </div>
          <SloField v-if="authenticated && paymentDetails.length > 0" class="mt-lg">
            <SloLabel for="save-payment-details" class="flex items-center justify-start gap-sm m-0">
              <SloInput v-model="savePaymentDetails" id="save-payment-details" type="checkbox" :value="true" />
              {{ 'Save payment details to my account' | t }}
            </SloLabel>
          </SloField>
        </div>
      </div>
      <div class="marketplace-checkout--table my-lg">
        <header class="marketplace-checkout--table-header">
          <h2 class="text-xl fw-strong">{{ 'Billing Address' | t }}</h2>
        </header>
        <div class="marketplace-checkout--table-body padding-full">
          <SloField class="mb-lg">
            <SloLabel for="address-same-as-billing" class="flex items-center justify-start gap-sm m-0">
              <SloInput
                v-model="addressSameAsBilling"
                type="checkbox"
                :value="true"
                :checked="addressSameAsBilling"
                id="address-same-as-billing"
              />
              {{ 'Same as shipping address' | t  }}
            </SloLabel>
          </SloField>
          <AddressFormFields
            v-if="!addressSameAsBilling"
            :original-address="address"
            :countries="billingCountries"
            :regions="billingCountryRegions"
            :errors="addressErrors"
          />
          <div v-else class="delivery-address c-gray-700">
            <p>{{ cartResponse?.deliveryAddress?.firstName }}</p>
            <p>{{ cartResponse?.deliveryAddress?.line1 }}</p>
            <p>{{ cartResponse?.deliveryAddress?.line2 }}</p>
            <p>
              {{ cartResponse?.deliveryAddress?.town }},
              {{ cartResponse?.deliveryAddress?.region?.isocodeShort || cartResponse?.deliveryAddress?.region?.isocode }}
            </p>
            <p>
              {{ cartResponse?.deliveryAddress?.postalCode }},
              {{ cartResponse?.deliveryAddress?.country?.isocode }}
            </p>
          </div>
        </div>
      </div>
    </SloLoader>
    <div v-if="isFormButtonsVisible">
      <div class="marketplace-checkout--table mt-lg">
        <header class="marketplace-checkout--table-header">
          <h2 class="text-xl fw-strong">{{ 'Customer PO Number' | t }}</h2>
        </header>
        <div class="marketplace-checkout--table-body padding-full">
          <SloField>
            <SloLabel>{{ 'Customer PO Number (optional)' | t }}</SloLabel>
            <SloInput
              @input="$emit('poNumberUpdate', $event)"
              :value="poNumber"
              :disabled="loading"
              type="text"
              name="poNumber"
            />
          </SloField>
        </div>
      </div>
      <div class="flex items-center justify-end gap-md mt-lg">
        <SloButton
          @click="handeCancelOrBack"
          :disabled="loading"
          type="button"
          fill="outline"
        >
          {{ shouldCancel ? 'Cancel' : 'Back' | t }}
        </SloButton>
        <SloButton :disabled="loading" type="submit" class="fixed-button-width">
          <SloLoader :loading="loading" color="white" size="md">
            {{ 'Continue to Review' | t }}
          </SloLoader>
        </SloButton>
      </div>
    </div>
  </form>
</template>

<script>
import {mapState, mapGetters} from 'vuex';
import t from '../../main/i18n';
import {
  getCardNumberLastFourDigits,
  getCardTypeIconName,
  formatSingleLineAddress,
  handleApiErrors,
  isCardExpired
} from '../../main/utils';
import {marketplaceApi} from '../apis/marketplace';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import AddressFormFields from './AddressFormFields.vue';

export default {
  name: 'CheckoutStep4',
  components: {
    AddressFormFields,
    SloLoader,
    SloButton,
    SloField,
    SloLabel,
    SloInput,
    SloIcon
  },
  filters: {
    t,
    getCardNumberLastFourDigits,
    getCardTypeIconName,
    formatSingleLineAddress
  },
  props: {
    stepChange: {type: Boolean, default: false},
    poNumber: {type: String, required: false}
  },
  data() {
    return {
      loading: false,
      paymetricAccessInfoLoading: false,
      paymetricAccessInfo: {},
      showPaymentForm: false,
      paymentDetailsLoading: false,
      paymentDetails: [],
      paymentDetailId: null,
      addressSameAsBilling: false,
      savePaymentDetails: false,
      billingCountries: [],
      billingCountryRegions: [],
      address: {},
      addressErrors: []
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    ...mapGetters('marketplace', ['cartHasRestrictedMessages', 'cartDeliveryModes']),
    ...mapState('marketplace', ['cartResponse']),
    isFormButtonsVisible() {
      return (this.authenticated && !this.paymentDetailsLoading && !this.paymetricAccessInfoLoading)
        || (!this.authenticated && !this.paymetricAccessInfoLoading);
    },
    submitPaymentDetailAsDisposable() {
      return !this.authenticated || (!this.savePaymentDetails && this.paymentDetails.length > 0);
    },
    shouldCancel() {
      return this.showPaymentForm && this.authenticated;
    }
  },
  created() {
    const paymetricIframeScript = document.createElement('script');
    paymetricIframeScript.setAttribute(
      'src',
      'https://cert-xiecomm.paymetric.com/diecomm/scripts/XIFrame/XIFrame-1.2.0.js'
    );
    document.head.appendChild(paymetricIframeScript);

    if (!this.cartDeliveryModes) {
      this.$emit('continue', 'back');
      this.$snotify.error(t('Please select a shipping method'));
    }

    this.resetAddress();
  },
  async mounted() {
    if (this.authenticated) {
      this.paymentDetailsLoading = true;
      await marketplaceApi.getCurrentCartPaymentDetails()
        .then(res => {
          this.paymentDetails = res.data.payments;
          this.paymentDetailsLoading = false;
          this.paymentDetailId = this.cartResponse?.paymentInfo
            ? this.cartResponse?.paymentInfo?.id
            : this.paymentDetails.find(paymentDetail => paymentDetail?.defaultPayment)?.id;
        })
        .catch(() => {
          this.paymentDetailsLoading = false;
          this.$snotify.error(t('Error loading your payment details'));
        });
    }

    await marketplaceApi.getCountryCodes('BILLING')
      .then(async res => {
        this.billingCountries = res.data?.countries;
        this.address.country.isocode = 'US';
        await this.getCountryRegions();
      })
      .catch(() => {
        this.shippingCountries = [];
        this.address.country.isocode = 'US';
      });

    if (!this.paymentDetails.length) this.showPaymentForm = true;
  },
  watch: {
    async 'address.country.isocode'(newValue, oldValue) {
      if (newValue !== oldValue) {
        await this.getCountryRegions();
      }
    },
    addressSameAsBilling(newValue) {
      if (newValue) {
        this.address = this.cartResponse?.deliveryAddress;
      } else {
        this.resetAddress();
      }
    },
    async showPaymentForm(newValue) {
      if (newValue) await this.getPaymetricAccessInfo();
      if (!newValue) this.resetAddress();
    }
  },
  methods: {
    async getPaymetricAccessInfo() {
      this.paymetricAccessInfoLoading = true;
      await marketplaceApi.getPaymetricAccessInfo()
        .then(res => {
          if (!res?.data) {
            this.paymetricAccessInfoLoading = false;
            this.$snotify.error(t('We are unable to process payments at this time'));
            return;
          }
          this.paymetricAccessInfo = res.data;
          this.paymetricAccessInfoLoading = false;
        })
        .catch(() => {
          this.paymetricAccessInfoLoading = false;
          this.$snotify.error(t('We are unable to process payments at this time'));
        });
    },
    async getCountryRegions() {
      this.address.region.isocode = '';
      this.address.region.isocodeShort = '';
      await marketplaceApi.getCountryRegions(this.address.country.isocode)
        .then(res => this.billingCountryRegions = res.data?.regions)
        .catch(() => this.billingCountryRegions = []);
    },
    isPaymentMethodChecked(paymentMethod) {
      if (this.paymentDetailId === paymentMethod.id)
        return true;
      if (!this.paymentDetailId === paymentMethod.id && paymentMethod.defaultPayment)
        return true;
    },
    handeCancelOrBack() {
      if (this.shouldCancel) {
        this.showPaymentForm = false;
        this.resetAddress();
        window.scrollTo(0, 0);
        return;
      }
      this.$emit('continue', 'back');
    },
    resetAddress() {
      this.address = {
        firstName: '',
        companyName: '',
        country: {isocode: 'US'},
        line1: '',
        line2: '',
        town: '',
        region: {isocode: '', isocodeShort: ''},
        postalCode: ''
      };
    },
    async handleContinue() {
      if (this.paymentDetailId && !this.showPaymentForm) {
        this.loading = true;

        if (this.cartHasRestrictedMessages) {
          this.$emit('restrictedMessagesModalToggle', true);
          this.loading = false;
          return false;
        }

        await marketplaceApi.selectCartPaymentDetail(this.paymentDetailId)
          .then(() => this.$emit('continue'))
          .catch(() => {
            this.loading = false;
            this.$snotify.error(t('Error selecting payment option'));
          });
        return;
      }

      const self = this;
      // eslint-disable-next-line no-undef
      $XIFrame.validate({
        iFrameId: 'paymetric-iframe',
        targetUrl: this.paymetricAccessInfo.paymetricsIframeAccessTokenUrl,
        async onValidate(valid) {
          if (valid) {
            if (!await self.validateAddress()) return;
            await self.submitPaymetricForm();
          } else {
            document.getElementById('new-payment-details').scrollIntoView({block: 'center'});
          }
        }
      });
    },
    async validateAddress() {
      if (this.address.country.isocode !== 'US') return true;

      this.loading = true;
      this.addressErrors = [];
      return await marketplaceApi.validateAddressWithStrikeIron(this.address)
        .then(res => {
          if (res.data) {
            // Update address data with validated address from StrikeIron
            this.address.line1 = res.data.line1;
            this.address.line2 = res.data.line2;
            this.address.town = res.data.town;
            this.address.region.isocode = res.data.region.isocode;
            this.address.region.isocodeShort = res.data.region.isocodeShort;
            this.address.postalCode = res.data.postalCode;
            return true;
          } else {
            this.loading = false;
            this.$snotify.clear();
            this.$snotify.error(t('Invalid address provided'));
            return false;
          }
        })
        .catch(e => {
          this.loading = false;
          if (e?.response?.data?.errors) {
            this.addressErrors = e?.response?.data?.errors;
          }
          this.$snotify.clear();
          this.$snotify.error(t('Invalid address provided'));
          return false;
        });
    },
    async submitPaymetricForm() {
      const self = this;
      // eslint-disable-next-line no-undef
      $XIFrame.submit({
        iFrameId: 'paymetric-iframe',
        targetUrl: this.paymetricAccessInfo.paymetricsIframeAccessTokenUrl,
        async onSuccess(msg) {
          const message = JSON.parse(msg);

          if (message && !message?.data?.HasPassed) {
            self.loading = false;
            self.$snotify.error(t('Invalid credit card credentials provided'));
          }

          if (message && message?.data?.HasPassed) {
            self.loading = true;
            await marketplaceApi.addCartPaymentDetail(
              self.paymetricAccessInfo.paymetricsIframeToken,
              self.address,
              self.submitPaymentDetailAsDisposable
            )
              .then(async() => {
                if (self.cartHasRestrictedMessages) {
                  self.$emit('restrictedMessagesModalToggle', true);
                  await self.getPaymetricAccessInfo();
                  self.loading = false;
                  return false;
                }

                self.$emit('continue');
              })
              .catch(async e => {
                await self.getPaymetricAccessInfo();
                self.loading = false;
                handleApiErrors(e, self, 'Unable to add payment method');
              });
          }
        }
      });
    },
    isCardExpired(paymentDetail) {
      return isCardExpired(paymentDetail);
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace-checkout {
  &--saved-payment-methods {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    list-style: none;
    padding: 0;
  }

  &--saved-payment-method {
    position: relative;
    display: flex;
    align-items: start;
    justify-content: start;
    gap: 1.6rem;
    padding: 1.6rem;
    border: 1px solid $color-gray-400;
    border-radius: 0.8rem;
    transition: border-color 0.15s ease-in, box-shadow 0.15s ease-in;

    &:hover {
      border-color: $color-gray-600;
    }

    &.checked {
      border-color: $color-blue-400;
      box-shadow: inset 0 0 0 1px $color-blue-400;

      .slo-icon:not(.card-icon) {
        opacity: 1;
        scale: 1;
      }

      .slo-icon.card-icon {
        filter: none;
      }

      .saved-payment-method--title {
        color: $color-blue-400;
      }
    }

    input {
      position: absolute;
      inset: 0;
      height: 100%;
      width: 100%;
      margin: 0;
      opacity: 0;
      cursor: pointer;
    }

    label {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
      font-size: 1.6rem;
      font-weight: 400;
      margin: 0;
    }

    .slo-icon:not(.card-icon) {
      position: absolute;
      top: 1.6rem;
      right: 1.6rem;
      color: $color-blue-400;
      opacity: 0;
      scale: 0.6;
      transition: opacity 0.15s ease-in, scale 0.15s ease-in;
    }

    .slo-icon.card-icon {
      font-size: 36px;
      width: 36px !important;
      height: 36px !important;
      filter: grayscale(1) opacity(0.6);
      transition: filter 0.15s ease-in;
    }

    .saved-payment-method--title {
      max-width: 22ch;
      color: $color-gray-900;
      font-weight: 500;
      transition: color 0.15s ease-in;

      @media (min-width: $screen-sm) {
        max-width: 100%;
      }
    }
  }

  .delivery-address p {
    margin: 0;
  }
}

.paymetric-container {
  min-height: 500px;
  max-height: 508px;
  overflow: hidden;

  @media (min-width: 480px) {
    min-height: 296px;
    max-height: 296px;
  }

  #paymetric-iframe {
    height: 100vh;
  }
}

.fixed-button-width {
  flex-basis: 160px;
}
</style>
